import React, { useEffect, useMemo, useState } from 'react';
import AssetCard from './AssetCard';
import { parsePrice } from '../utils/currency';
import { formatListingFieldValue, listingTypeConfigFor } from '../utils/listingTypes';

const normaliseColumns = (columns) => {
  const parsedColumns = Number(columns);
  if (!Number.isFinite(parsedColumns)) return 3;
  return Math.min(Math.max(Math.round(parsedColumns), 1), 4);
};

function AssetGallery({
  addToCartDisabled = false,
  assets,
  assetCardBackgroundColor,
  assetFields,
  cartButtonLabel,
  layout,
  columns,
  link,
  listingType,
  permissions,
  searchQuery = '',
  showShareAction = true,
  shareUrl,
  editable = false,
  enableHtmlEmbedFullscreen = false,
  enablePdfPresentation = false,
  onAssetChange,
  onAssetDelete,
  onAssetMove,
  onAddToCart,
  onAddToItemForm,
  itemFormButtonText,
  onAddUpdate,
  onEnquire,
  onShare,
  shareDescription,
  shareTitle,
  showListingFilters = true,
  showStoreQuantityHint = false,
}) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeListingFilters, setActiveListingFilters] = useState({});
  const [activeRangeFilters, setActiveRangeFilters] = useState({});
  const [showMoreListingFilters, setShowMoreListingFilters] = useState(false);
  const listingConfig = useMemo(() => (
    listingType ? listingTypeConfigFor(listingType) : null
  ), [listingType]);
  const rangeFilterFields = useMemo(() => {
    if (listingType === 'property' || listingType === 'accommodation') {
      return [
        { id: 'price', label: 'Price', source: 'price' },
      ];
    }

    if (listingType === 'vehicle') {
      return [
        { id: 'price', label: 'Price', source: 'price' },
        { id: 'mileage', label: 'Mileage', source: 'listingField', suffix: 'km' },
      ];
    }

    return [];
  }, [listingType]);
  const listingFilterFields = useMemo(() => (
    listingType === 'property'
      ? [
        { id: 'area', label: 'Area', source: 'asset', assetField: 'area', type: 'text', filterable: true },
        ...(listingConfig?.fields?.filter((field) => field.filterable) || []),
      ]
      : listingConfig?.fields?.filter((field) => field.filterable) || []
  ), [listingConfig, listingType]);
  const listingFilterOptions = useMemo(() => (
    listingFilterFields.map((field) => {
      const optionAssets = listingType === 'vehicle' && field.id === 'model' && activeListingFilters.make
        ? (assets || []).filter((asset) => String(asset.listingFields?.make || '').trim() === activeListingFilters.make)
        : (assets || []);
      const values = [...new Set(optionAssets
        .map((asset) => String(
          field.source === 'asset'
            ? asset[field.assetField || field.id]
            : asset.listingFields?.[field.id] || '',
        ).trim())
        .filter(Boolean))]
        .sort((firstValue, secondValue) => {
          if (field.type === 'number') return (Number(firstValue) || 0) - (Number(secondValue) || 0);
          return firstValue.localeCompare(secondValue);
        });

      return {
        ...field,
        values,
      };
    }).filter((field) => (
      field.values.length > 1 ||
      (listingType === 'vehicle' && field.id === 'model' && activeListingFilters.make && field.values.length > 0)
    ))
  ), [activeListingFilters.make, assets, listingFilterFields, listingType]);
  const orderedListingFilterOptions = useMemo(() => {
    if (listingType !== 'vehicle') return listingFilterOptions;

    const vehicleFilterOrder = ['make', 'model', 'year', 'transmission', 'fuel', 'drivetrain'];
    return [...listingFilterOptions].sort((firstField, secondField) => (
      vehicleFilterOrder.indexOf(firstField.id) - vehicleFilterOrder.indexOf(secondField.id)
    ));
  }, [listingFilterOptions, listingType]);
  const primaryListingFilterOptions = useMemo(() => (
    listingType === 'vehicle'
      ? orderedListingFilterOptions.filter((field) => ['make', 'model', 'year'].includes(field.id))
      : orderedListingFilterOptions
  ), [listingType, orderedListingFilterOptions]);
  const secondaryListingFilterOptions = useMemo(() => (
    listingType === 'vehicle'
      ? orderedListingFilterOptions.filter((field) => !['make', 'model', 'year'].includes(field.id))
      : []
  ), [listingType, orderedListingFilterOptions]);
  const hasSecondaryListingFilters = secondaryListingFilterOptions.length > 0 || rangeFilterFields.length > 0;
  const visibleListingFilterOptions = showMoreListingFilters
    ? [...primaryListingFilterOptions, ...secondaryListingFilterOptions]
    : primaryListingFilterOptions;
  const visibleRangeFilterFields = listingType === 'vehicle' && !showMoreListingFilters
    ? []
    : rangeFilterFields;
  const assetCategories = useMemo(() => (
    [...new Set((assets || [])
      .map((asset) => String(asset.category || '').trim())
      .filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [assets]);
  const filteredAssets = useMemo(() => (
    (assets || [])
      .map((asset, index) => ({ asset, index }))
      .filter(({ asset }) => (
        activeCategory === 'all' || String(asset.category || '').trim() === activeCategory
      ))
      .filter(({ asset }) => (
        Object.entries(activeListingFilters).every(([fieldId, value]) => (
          !value || String(
            listingFilterFields.find((field) => field.id === fieldId)?.source === 'asset'
              ? asset[listingFilterFields.find((field) => field.id === fieldId)?.assetField || fieldId]
              : asset.listingFields?.[fieldId] || '',
          ).trim() === value
        ))
      ))
      .filter(({ asset }) => (
        rangeFilterFields.every((field) => {
          const minFilter = activeRangeFilters[`${field.id}Min`];
          const maxFilter = activeRangeFilters[`${field.id}Max`];
          const hasMin = minFilter !== undefined && minFilter !== '';
          const hasMax = maxFilter !== undefined && maxFilter !== '';
          const min = Number(minFilter);
          const max = Number(maxFilter);
          if (!hasMin && !hasMax) return true;
          if ((hasMin && !Number.isFinite(min)) || (hasMax && !Number.isFinite(max))) return true;

          const rawValue = field.source === 'price'
            ? asset.price
            : asset.listingFields?.[field.id];
          if (String(rawValue || '').trim() === '') return false;

          const value = field.source === 'price'
            ? parsePrice(rawValue)
            : Number(String(rawValue).replace(/[^0-9.-]/g, ''));

          if (!Number.isFinite(value)) return false;
          if (hasMin && value < min) return false;
          if (hasMax && value > max) return false;
          return true;
        })
      ))
      .filter(({ asset }) => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return true;
        const searchableValue = JSON.stringify(asset, (key, value) => (
          typeof value === 'string' && /^(data:|https?:)/i.test(value) ? undefined : value
        )).toLowerCase();
        return searchableValue.includes(query);
      })
  ), [activeCategory, activeListingFilters, activeRangeFilters, assets, listingFilterFields, rangeFilterFields, searchQuery]);

  useEffect(() => {
    if (activeCategory === 'all') return;
    if (assetCategories.includes(activeCategory)) return;
    setActiveCategory('all');
  }, [activeCategory, assetCategories]);

  useEffect(() => {
    setActiveListingFilters((currentFilters) => {
      const validFilters = Object.fromEntries(
        listingFilterOptions.map((field) => [field.id, new Set(field.values)])
      );
      const nextFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([fieldId, value]) => (
          validFilters[fieldId]?.has(value)
        ))
      );

      return JSON.stringify(nextFilters) === JSON.stringify(currentFilters) ? currentFilters : nextFilters;
    });
  }, [listingFilterOptions]);

  if (!assets?.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
        Upload assets to build the share page gallery.
      </div>
    );
  }

  const resolvedColumns = assets.length === 1 ? 1 : normaliseColumns(columns);
  const comboProductSourceAssets = Array.isArray(link?.assets) && link.assets.length
    ? link.assets
    : assets || [];
  const comboProductOptions = comboProductSourceAssets
    .filter((asset) => asset?.id)
    .map((asset) => ({
      id: String(asset.id),
      title: asset.title || asset.name || 'Untitled product',
      name: asset.name || '',
      category: asset.category || '',
      price: asset.price || '',
      storeBasePrice: asset.storeBasePrice || '',
      discountType: asset.discountType || 'percentage',
      discountValue: asset.discountValue || '',
      reducedPrice: asset.reducedPrice || '',
      unitCost: asset.unitCost || '',
      profit: asset.profit || '',
    }));
  const effectiveLayout = enablePdfPresentation ? 'grid' : layout;
  const galleryClassName = [
    effectiveLayout === 'masonry' ? 'asset-masonry' : 'asset-grid',
    `asset-columns-${resolvedColumns}`,
    assets.length === 1 ? 'asset-single' : '',
  ].filter(Boolean).join(' ');
  const renderAsset = (asset, index) => (
    <AssetCard
      addToCartDisabled={addToCartDisabled}
      asset={asset}
      assetCardBackgroundColor={assetCardBackgroundColor || link?.assetCardBackgroundColor}
      assetCount={assets.length}
      assetFields={assetFields}
      assetIndex={index}
      cartButtonLabel={cartButtonLabel}
      comboProductOptions={comboProductOptions.filter((product) => product.id !== String(asset.id))}
      editable={editable}
      enableHtmlEmbedFullscreen={enableHtmlEmbedFullscreen}
      enablePdfPresentation={enablePdfPresentation}
      isFirstAsset={index === 0}
      isLastAsset={index === assets.length - 1}
      listingType={listingType}
      link={link}
      key={asset.id}
      onAssetChange={onAssetChange}
      onAssetDelete={onAssetDelete}
      onAssetMove={onAssetMove}
      onAddToCart={onAddToCart}
      onAddToItemForm={onAddToItemForm}
      itemFormButtonText={itemFormButtonText}
      onAddUpdate={onAddUpdate}
      onEnquire={onEnquire}
      onShare={onShare}
      permissions={permissions}
      shareDescription={shareDescription}
      shareTitle={shareTitle}
      showStoreQuantityHint={showStoreQuantityHint}
      showShareAction={showShareAction}
      shareUrl={shareUrl}
    />
  );
  const galleryContent = !filteredAssets.length ? (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      No items match these filters.
    </div>
  ) : effectiveLayout === 'masonry' ? (
    <div className={galleryClassName}>
      {Array.from({ length: resolvedColumns }, () => []).map((_, columnIndex) => (
        <div className="asset-masonry-column" key={`masonry-column-${columnIndex + 1}`}>
          {filteredAssets
            .filter((_, assetIndex) => assetIndex % resolvedColumns === columnIndex)
            .map(({ asset, index }) => renderAsset(asset, index))}
        </div>
      ))}
    </div>
  ) : (
    <div className={galleryClassName}>
      {filteredAssets.map(({ asset, index }) => renderAsset(asset, index))}
    </div>
  );

  return (
    <div className="grid gap-4">
      {assetCategories.length > 0 && (
        <div className="asset-category-filter" aria-label="Filter items by category">
          <button
            className={activeCategory === 'all' ? 'active' : ''}
            onClick={() => setActiveCategory('all')}
            type="button"
          >
            All
          </button>
          {assetCategories.map((category) => (
            <button
              className={activeCategory === category ? 'active' : ''}
              key={category}
              onClick={() => setActiveCategory(category)}
              type="button"
            >
              {category}
            </button>
          ))}
        </div>
      )}
      {showListingFilters && (listingFilterOptions.length > 0 || rangeFilterFields.length > 0) && (
        <div className="flex flex-wrap items-end gap-3 rounded border border-slate-200 bg-white p-3" aria-label={`${listingConfig.label} filters`}>
          {visibleListingFilterOptions.map((field) => {
            const isCompactVehicleFilter = listingType === 'vehicle' && ['year', 'fuel', 'drivetrain'].includes(field.id);
            const isCompactPropertyFilter = listingType === 'property' && ['area', 'bedrooms', 'bathrooms', 'parking'].includes(field.id);

            return (
            <div className={isCompactVehicleFilter || isCompactPropertyFilter || (listingType === 'vehicle' && ['make', 'model'].includes(field.id)) ? 'w-full sm:w-44' : 'w-full sm:w-80 lg:w-96'} key={field.id}>
              <label className="field-label mt-0 text-[10px]" htmlFor={`listing-filter-${listingType}-${field.id}`}>
                {field.label}
              </label>
              <select
                className="field"
                id={`listing-filter-${listingType}-${field.id}`}
                value={activeListingFilters[field.id] || ''}
                onChange={(event) => setActiveListingFilters({
                  ...activeListingFilters,
                  [field.id]: event.target.value,
                })}
              >
                <option value="">All</option>
                {field.values.map((value) => (
                  <option key={value} value={value}>
                    {formatListingFieldValue(field, value)}
                  </option>
                ))}
              </select>
            </div>
            );
          })}
          {visibleRangeFilterFields.map((field) => (
            <div className="grid w-full grid-cols-2 gap-2 sm:w-56" key={field.id}>
              <div>
                <label className="field-label mt-0 text-[10px]" htmlFor={`listing-filter-${listingType}-${field.id}-min`}>
                  Min {field.label}
                </label>
                <input
                  className="field"
                  id={`listing-filter-${listingType}-${field.id}-min`}
                  inputMode="numeric"
                  min="0"
                  placeholder="Min"
                  type="number"
                  value={activeRangeFilters[`${field.id}Min`] || ''}
                  onChange={(event) => setActiveRangeFilters({
                    ...activeRangeFilters,
                    [`${field.id}Min`]: event.target.value,
                  })}
                />
              </div>
              <div>
                <label className="field-label mt-0 text-[10px]" htmlFor={`listing-filter-${listingType}-${field.id}-max`}>
                  Max {field.label}
                </label>
                <input
                  className="field"
                  id={`listing-filter-${listingType}-${field.id}-max`}
                  inputMode="numeric"
                  min="0"
                  placeholder="Max"
                  type="number"
                  value={activeRangeFilters[`${field.id}Max`] || ''}
                  onChange={(event) => setActiveRangeFilters({
                    ...activeRangeFilters,
                    [`${field.id}Max`]: event.target.value,
                  })}
                />
              </div>
            </div>
          ))}
          {listingType === 'vehicle' && hasSecondaryListingFilters && (
            <button
              className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 sm:w-auto"
              onClick={() => setShowMoreListingFilters((current) => !current)}
              type="button"
            >
              {showMoreListingFilters ? 'Less filters' : 'More filters'}
            </button>
          )}
        </div>
      )}
      {galleryContent}
      </div>
  );
}

export default AssetGallery;
